import React, { useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Stack, Chip, Button, CircularProgress } from "@mui/material";
import PsychologistApi, { Psychologist } from "../api/PsychologistApi";
import { TOPIC_KEYS, REGION_KEYS, eur } from "../constants/funnel";

// Publieke directory-pagina's (/psychologen[/:region[/:topic]]): organische
// vindbaarheid per regio × thema ("psycholoog burn-out Vlaanderen — online").
// Belofte privacystandaard blijft gelden: geen tracking — dit zijn gewoon
// server-loze publieke lijstpagina's met nette titels en interne links.
// De echte funnel (/hulp-vinden) blijft de aanbevolen route; dit is de
// zij-ingang voor zoekverkeer.

const SEO_TOPICS = TOPIC_KEYS.filter((t) => t !== "unsure");
const SEO_REGIONS = REGION_KEYS.filter((r) => r !== "other");

export default function Directory() {
  const { region, topic } = useParams<{ region?: string; topic?: string }>();
  const { t, i18n } = useTranslation();
  const [list, setList] = useState<Psychologist[] | null>(null);

  const validRegion = region && (SEO_REGIONS as readonly string[]).includes(region) ? region : undefined;
  const validTopic = topic && (SEO_TOPICS as readonly string[]).includes(topic) ? topic : undefined;
  const regionLabel = validRegion ? t(`regions.${validRegion}`) : "";
  const topicLabel = validTopic ? t(`topics.${validTopic}.label`) : "";

  useEffect(() => {
    PsychologistApi.list(validTopic, validRegion).then(setList).catch(() => setList([]));
  }, [validTopic, validRegion]);

  // Document-title + meta description per pagina (geen SSR nodig: Google
  // rendert JS; de sitemap wijst de crawler de weg).
  useEffect(() => {
    const parts = [t("directory.titleBase")];
    if (topicLabel) parts.unshift(topicLabel);
    if (regionLabel) parts.push(regionLabel);
    document.title = `${parts.join(" — ")} | Carefunded`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", t("directory.metaDescription", { topic: topicLabel || t("directory.anyTopic"), region: regionLabel || "België" }));
    return () => { document.title = "Carefunded"; };
  }, [t, topicLabel, regionLabel, i18n.language]);

  return (
    <Box sx={{ bgcolor: "background.default", pb: 10 }}>
      <Container maxWidth="md" sx={{ pt: { xs: 5, sm: 8 } }}>
        <Typography variant="h1" sx={{ fontSize: { xs: "1.9rem", sm: "2.4rem" }, mb: 1 }}>
          {t("directory.heading", { topic: topicLabel || t("directory.anyTopic"), region: regionLabel || "België" })}
        </Typography>
        <Typography sx={{ color: "text.secondary", mb: 3, maxWidth: 640 }}>{t("directory.intro")}</Typography>

        <Button component={RouterLink} to="/hulp-vinden" variant="contained" sx={{ mb: 4 }}>{t("directory.cta")}</Button>

        {list === null ? (
          <CircularProgress size={24} sx={{ color: "primary.main" }} />
        ) : list.length === 0 ? (
          <Typography sx={{ color: "text.secondary" }}>{t("directory.none")}</Typography>
        ) : (
          <Stack spacing={2} sx={{ mb: 5 }}>
            {list.map((p: any) => (
              <Box key={p._id} sx={{ p: 2.5, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)", display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography sx={{ fontWeight: 600, m: 0 }}>{p.displayName}</Typography>
                  <Typography sx={{ fontSize: ".88rem", color: "text.secondary", m: 0 }}>
                    {(p.specializations || []).slice(0, 3).map((s: string) => t(`topics.${s}.label`)).join(" · ")}
                  </Typography>
                </Box>
                {p.therapyRateCents && <Chip size="small" variant="outlined" label={`${t("directory.fromRate")} ${eur(p.coachingRateCents ?? p.rateCents)}`} />}
                <Button component={RouterLink} to={`/boek/${p._id}`} size="small" variant="outlined">{t("directory.view")}</Button>
              </Box>
            ))}
          </Stack>
        )}

        {/* Interne links: regio's × thema's — de crawler (en de bezoeker) kan
            elke combinatie bereiken vanaf elke pagina. */}
        <Typography variant="h2" sx={{ fontSize: "1.15rem", mb: 1.5 }}>{t("directory.browse")}</Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, mb: 2 }}>
          {SEO_REGIONS.map((r) => (
            <Chip key={r} component={RouterLink} to={validTopic ? `/psychologen/${r}/${validTopic}` : `/psychologen/${r}`} clickable
              label={t(`regions.${r}`)} variant={r === validRegion ? "filled" : "outlined"} color={r === validRegion ? "primary" : "default"} />
          ))}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
          {SEO_TOPICS.map((tp) => (
            <Chip key={tp} component={RouterLink} to={validRegion ? `/psychologen/${validRegion}/${tp}` : `/psychologen/flanders/${tp}`} clickable
              label={t(`topics.${tp}.label`)} variant={tp === validTopic ? "filled" : "outlined"} color={tp === validTopic ? "primary" : "default"} />
          ))}
        </Stack>
      </Container>
    </Box>
  );
}
